import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { Suspense } from "react";
import { useState } from "react";
import { FaGithub } from "react-icons/fa";
import { toast } from "sonner";

import { useOrganizationCanManage } from "@/hooks/organizations/use-organization-can-manage";
import { useTRPC } from "@/infrastructure/trpc/react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { ClientDate } from "@/shared/ui/client-date";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Skeleton } from "@/shared/ui/skeleton";
export const Route = createFileRoute(
  "/app/organization/$id/settings/github/"
)({
  loader: async ({ context, params }) => {
    await context.queryClient
      .query(
        context.trpc.githubApp.getInstallation.queryOptions(
          {
            organizationId: params.id,
          }
        )
      )
      .catch(() => undefined);
    // Fire-and-forget: don't block navigation, client query
    // handles enabled guard (canManage && !!installation)
    void context.queryClient
      .query(
        context.trpc.githubApp.checkInstallationHealth.queryOptions(
          {
            organizationId: params.id,
          }
        )
      )
      .catch(() => undefined);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [showRemoveDialog, setShowRemoveDialog] =
    useState(false);

  const { canManage } = useOrganizationCanManage(id);

  const {
    data: installation,
    isLoading: isLoadingInstallation,
  } = useSuspenseQuery(
    trpc.githubApp.getInstallation.queryOptions({
      organizationId: id,
    })
  );

  const { data: installationUrlData } = useQuery({
    ...trpc.githubApp.getInstallationUrl.queryOptions({
      organizationId: id,
    }),
    enabled: canManage,
  });

  const {
    data: installationHealth,
    isFetching: isCheckingInstallationHealth,
    refetch: refetchInstallationHealth,
  } = useQuery({
    ...trpc.githubApp.checkInstallationHealth.queryOptions({
      organizationId: id,
    }),
    enabled: canManage && !!installation,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const removeInstallation = useMutation(
    trpc.githubApp.removeInstallation.mutationOptions({
      onSuccess: async () => {
        toast.success("GitHub App disconnected");
        setShowRemoveDialog(false);
        await queryClient.invalidateQueries({
          queryKey:
            trpc.githubApp.getInstallation.queryKey(),
        });
      },
      onError: (error) => {
        toast.error(
          error.message || "Failed to disconnect GitHub App"
        );
      },
    })
  );

  const handleInstall = () => {
    if (installationUrlData?.url) {
      window.open(installationUrlData.url, "_blank");
    }
  };

  if (isLoadingInstallation) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
        </div>
      }
    >
      <div className="space-y-6">
        <div>
          <h3 className="mb-1 text-lg font-semibold">
            GitHub Integration
          </h3>
          <p className="text-sm text-muted-foreground">
            Connect GitHub to automatically create issues
            when K8s problems are detected. Claude Code can
            then analyze these issues and create pull
            requests with fixes.
          </p>
        </div>

        {/* Installation Status Card */}
        <Card>
          <CardHeader variant="compact">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FaGithub className="h-5 w-5" />
                <CardTitle variant="base">
                  GitHub App
                </CardTitle>
              </div>
              {installation ? (
                <Badge variant="success">
                  <CheckCircle2 className="mr-1 h-3 w-3" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="secondary">
                  Not connected
                </Badge>
              )}
            </div>
            <CardDescription>
              {installation
                ? `Connected to @${installation.accountLogin}`
                : "Install the vivus GitHub App to enable automatic issue creation"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {installation ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">
                      Account
                    </p>
                    <p className="font-medium">
                      @{installation.accountLogin}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">
                      Account Type
                    </p>
                    <p className="font-medium">
                      {installation.accountType}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">
                      Repository Access
                    </p>
                    <p className="font-medium capitalize">
                      {installation.repositorySelection}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">
                      Connected Since
                    </p>
                    <ClientDate
                      date={installation.createdAt}
                      format="date"
                      variant="medium"
                    />
                  </div>
                </div>

                {canManage && (
                  <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/50 p-3 text-sm">
                    <div className="flex items-center gap-2">
                      {installationHealth?.connected ? (
                        <CheckCircle2 className="h-4 w-4 text-success-foreground" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-warning-foreground" />
                      )}
                      <span className="text-muted-foreground">
                        {installationHealth?.connected
                          ? "Verified with GitHub"
                          : "Not verified with GitHub"}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        void refetchInstallationHealth();
                      }}
                      disabled={
                        isCheckingInstallationHealth
                      }
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {canManage && (
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleInstall}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reconfigure
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setShowRemoveDialog(true);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Disconnect
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-lg bg-muted/50 p-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 text-warning-foreground" />
                  <div className="text-sm">
                    <p className="mb-1 font-medium">
                      GitHub App not installed
                    </p>
                    <p className="text-muted-foreground">
                      Install the vivus GitHub App to enable
                      automatic issue creation for K8s
                      problems. Claude Code will analyze
                      issues and create PRs with fixes.
                    </p>
                  </div>
                </div>

                {canManage && (
                  <Button onClick={handleInstall}>
                    <FaGithub className="mr-2 h-4 w-4" />
                    Install GitHub App
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* How it Works Card */}
        <Card>
          <CardHeader variant="compact">
            <CardTitle variant="base">
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                  1
                </span>
                <div>
                  <p className="font-medium">
                    Agent detects issues
                  </p>
                  <p className="text-muted-foreground">
                    Agents monitor your infrastructure and
                    detect problems that need attention.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                  2
                </span>
                <div>
                  <p className="font-medium">
                    GitHub Issue created automatically
                  </p>
                  <p className="text-muted-foreground">
                    vivus creates a GitHub issue with the
                    problem details and any configured bot
                    mentions.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                  3
                </span>
                <div>
                  <p className="font-medium">
                    Claude Code analyzes and fixes
                  </p>
                  <p className="text-muted-foreground">
                    Claude Code GitHub Action analyzes the
                    issue, investigates the codebase, and
                    creates a PR with the fix.
                  </p>
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Remove Dialog */}
        <Dialog
          open={showRemoveDialog}
          onOpenChange={setShowRemoveDialog}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Disconnect GitHub App
              </DialogTitle>
              <DialogDescription>
                This will remove the GitHub App connection
                for this organization. Automatic issue
                creation will be disabled for all clusters.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRemoveDialog(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  removeInstallation.mutate({
                    organizationId: id,
                  });
                }}
                disabled={removeInstallation.isPending}
              >
                {removeInstallation.isPending
                  ? "Disconnecting..."
                  : "Disconnect"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Suspense>
  );
}
